/**
 * FlightOptionsPanel — everything that can change about ONE flight, in one place.
 *
 * The trip page used to answer "what are my other choices?" three ways: a "date
 * options" chip, a dashed "Change", and a "Fares & alternatives" pulldown, plus a
 * whole second tab. This is the single answer. It opens from the one "Flight
 * options" control on the flight row and holds: the date (and re-checking it),
 * where the price has been, what moving a day would cost, and every other flight
 * with the means to pick one.
 */
import type { PlanLeg } from "@/lib/types";
import { formatTime } from "@/components/trips/itinerary/itinerary-utils";
import FareMatrixTable from "./FareMatrixTable";
import FlightOptionsList from "./FlightOptionsList";
import DateFlexPanel from "./DateFlexPanel";
import PriceHistoryStrip from "./PriceHistoryStrip";
import FareHistoryToggle from "./FareHistoryToggle";
import RouteDateEditor from "./RouteDateEditor";
import RouteRecheckButton from "./RouteRecheckButton";
import YourPriceControl from "./YourPriceControl";
import BestFareLine from "./BestFareLine";
import LegWatch from "./LegWatch";
import FlyDaysStrip from "./FlyDaysStrip";
import { hasPriceChoiceData, showFlyDays } from "./fareLadderView";

interface Props {
  tripId: string;
  leg: PlanLeg;
}

/** Cheapest fare across the option matrix, or null when there is nothing to price. */
export function cheapestFare(leg: Pick<PlanLeg, "matrix">): number | null {
  let best: number | null = null;
  for (const group of leg.matrix ?? []) {
    for (const f of group.flights ?? []) {
      if (typeof f.price === "number" && f.price > 0 && (best == null || f.price < best)) {
        best = f.price;
      }
    }
  }
  return best;
}

/** Total flights across every date option. */
export function countOptions(leg: Pick<PlanLeg, "matrix">): number {
  return (leg.matrix ?? []).reduce((n, g) => n + (g.flights?.length ?? 0), 0);
}

/**
 * "47 flights from $312" — and gracefully less when we know less ("" when we
 * know nothing). The control that opens this panel wears the same sentence, so
 * closed it already says what is behind it.
 */
export function optionsSummary(leg: Pick<PlanLeg, "matrix">): string {
  const bits: string[] = [];
  const n = countOptions(leg);
  const cheapest = cheapestFare(leg);
  if (n > 0) bits.push(n + (n === 1 ? " flight" : " flights"));
  if (cheapest != null) bits.push("from $" + Math.round(cheapest).toLocaleString());
  return bits.join(" ");
}

/** Cells actually present in the time-of-day grid. Mirrors FareMatrixTable. */
export function gridCellCount(matrix: PlanLeg["matrixV2"] | null | undefined): number {
  return (matrix?.gateways ?? []).reduce(
    (n, g) => n + (g.rows ?? []).reduce((m, r) => m + Object.keys(r.cells ?? {}).length, 0),
    0,
  );
}

/**
 * The trade, in one line, when the chosen flight is NOT the cheapest sensible
 * one: "AA 7:00 AM saves $45". "Sensible" mirrors the scorer's comfort bands: same
 * carrier, no more stops, no red-eye / overnight, not an unsociable early
 * departure (before 06:30) and no more than an hour slower. Null when the chosen
 * flight is already the cheapest of those.
 */
export function tradeNote(leg: Pick<PlanLeg, "focus" | "matrix">): string | null {
  const chosen = leg.focus;
  if (!chosen || chosen.price == null) return null;
  let best: { price: number; departLocal: string; airline: string | null } | null = null;
  for (const group of leg.matrix ?? []) {
    for (const f of group.flights ?? []) {
      if (f.flightId === chosen.flightId || typeof f.price !== "number" || f.price <= 0) continue;
      if (f.departDate !== chosen.departDate || f.airline !== chosen.airline) continue;
      if (f.price >= chosen.price || !f.departLocal || f.departLocal < "06:30") continue;
      if (f.redEye || f.overnight) continue;
      if ((f.stops ?? 0) > (chosen.stops ?? 0)) continue;
      if (f.durationMinutes != null && chosen.durationMinutes != null && f.durationMinutes > chosen.durationMinutes + 60) continue;
      if (!best || f.price < best.price) best = { price: f.price, departLocal: f.departLocal, airline: f.airline };
    }
  }
  if (!best) return null;
  const saves = Math.round(chosen.price - best.price);
  if (saves < 1) return null;
  return [best.airline, formatTime(best.departLocal)].filter(Boolean).join(" ") + " saves $" + saves.toLocaleString();
}

export default function FlightOptionsPanel({ tripId, leg }: Props) {
  const hasMatrix = (leg.matrix?.length ?? 0) > 0;
  /*
   * `matrixV2` is a truthy OBJECT even when it holds nothing — a leg with no
   * fares still ships `{timeBuckets:[…], gateways:[]}`. Mirror FareMatrixTable's
   * own emptiness test: count the cells.
   */
  const hasGrid = gridCellCount(leg.matrixV2) > 0;
  const unbooked = (leg.bookingStatus ?? "none") === "none";
  const priceControl = hasPriceChoiceData(leg);

  return (
    <div
      id={"flight-options-" + leg.routeId}
      className="space-y-3 rounded-lg border bg-muted/30 px-3 py-3 print:hidden"
    >
      <div className="flex flex-wrap items-center gap-2">
        <RouteDateEditor
          tripId={tripId}
          routeId={leg.routeId}
          date={leg.date}
          dateLabel={leg.dateLabel}
          editable={unbooked}
        />
        {unbooked && priceControl && <YourPriceControl tripId={tripId} leg={leg} />}
        <span className="ml-auto">
          <RouteRecheckButton tripId={tripId} routeId={leg.routeId} />
        </span>
      </div>

      {unbooked && <BestFareLine flight={leg.focus} />}

      {unbooked && <LegWatch leg={leg} />}

      {unbooked && tradeNote(leg) && (
        <p className="text-xs text-muted-foreground">{tradeNote(leg)}</p>
      )}

      {leg.history && <PriceHistoryStrip history={leg.history} />}
      {leg.focus?.dedupeKey && <FareHistoryToggle tripId={tripId} routeId={leg.routeId} dedupeKey={leg.focus.dedupeKey} />}
      <DateFlexPanel flex={leg.dateFlex} />

      {/* Price-by-time-of-day first: it is how you decide WHICH option to look
          at, so it sits above the list of options. */}
      {hasGrid && (
        <FareMatrixTable tripId={tripId} routeId={leg.routeId} matrix={leg.matrixV2} bare />
      )}
      {(leg.flyDays ?? []).filter((fd) => showFlyDays(fd, leg)).map((fd) => (
        <FlyDaysStrip key={fd.carrier} flyDays={fd} legDate={leg.date} />
      ))}
      {hasMatrix && (
        <FlightOptionsList
          tripId={tripId}
          routeId={leg.routeId}
          matrix={leg.matrix}
          originCode={leg.origin}
          destinationCode={leg.destination}
          role={leg.role}
          priceChoice={leg.priceChoice}
          bare
        />
      )}
      {!hasMatrix && !hasGrid && (
        <p className="text-xs text-muted-foreground">
          No other flights found yet. Re-check to look again.
        </p>
      )}
    </div>
  );
}
